import { create } from 'zustand';
import type { AppNotification } from '@/types';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from '@/lib/api';

const POLL_INTERVAL_MS = 60_000;

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  pollTimer: ReturnType<typeof setInterval> | null;

  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: string) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  clear: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  pollTimer: null,

  fetchNotifications: async () => {
    set({ isLoading: true });
    try {
      const response = await getNotifications(1, 20);
      set({ notifications: response.data || [], isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await getUnreadCount();
      set({ unreadCount: count });
    } catch {
      // Non-critical: keep the last known count.
    }
  },

  markRead: async (id: string) => {
    // Optimistic: flag read + decrement the badge.
    const wasUnread = get().notifications.find((n) => n.id === id && !n.isRead);
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }));
    try {
      await markNotificationRead(id);
    } catch {
      // Best-effort; the next poll reconciles the count.
    }
  },

  markAllRead: async () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }));
    try {
      await markAllNotificationsRead();
    } catch {
      // Best-effort; the next poll reconciles.
    }
  },

  remove: async (id: string) => {
    const { notifications } = get();
    const removed = notifications.find((n) => n.id === id);
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount: removed && !removed.isRead ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }));
    try {
      await deleteNotification(id);
    } catch {
      set({ notifications });
    }
  },

  startPolling: () => {
    if (get().pollTimer) return;
    void get().fetchUnreadCount();
    const timer = setInterval(() => {
      void get().fetchUnreadCount();
    }, POLL_INTERVAL_MS);
    set({ pollTimer: timer });
  },

  stopPolling: () => {
    const { pollTimer } = get();
    if (pollTimer) clearInterval(pollTimer);
    set({ pollTimer: null });
  },

  clear: () => {
    get().stopPolling();
    set({ notifications: [], unreadCount: 0 });
  },
}));
